import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ChevronLeft, ChevronRight, Phone, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const inr = (n) => {
  try { return `₹${Number(n || 0).toLocaleString('en-IN')}`; } catch { return `₹${n}`; }
};

const buildReminderBody = (r) => {
  const dueRaw = r.next_due_date ? new Date(r.next_due_date) : null;
  const due = dueRaw && !Number.isNaN(dueRaw.getTime())
    ? `${String(dueRaw.getDate()).padStart(2,'0')}/${String(dueRaw.getMonth()+1).padStart(2,'0')}/${dueRaw.getFullYear()}`
    : 'soon';
  const days = typeof r.days_remaining === 'number' ? r.days_remaining : '';
  return `Hi ${r.client_name || 'there'}, this is your agent from InsureGrowth. `
    + `Your policy ${r.policy_number} is due for renewal on ${due}`
    + (days !== '' ? ` — just ${days} day${days === 1 ? '' : 's'} away.` : '.')
    + ` Please let me know a convenient time to complete the renewal. Thank you.`;
};

const DayCell = ({ dueList, dayNum, isToday, sideMode, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState('');

  const send = async (renewal, channel) => {
    if (!renewal.client_phone) { toast.error('No mobile on file for this client'); return; }
    setSending(`${renewal.policy_id}-${channel}`);
    try {
      await axios.post(`${API}/messages/send`, {
        to: renewal.client_phone,
        body: buildReminderBody(renewal),
        channel,
        client_id: renewal.client_id,
      }, { withCredentials: true });
      toast.success(`${channel === 'sms' ? 'SMS' : 'WhatsApp'} sent to ${renewal.client_name}`);
    } catch (err) {
      console.error('Reminder send failed:', err);
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally { setSending(''); }
  };

  const hasDue = dueList.length > 0;
  const base = 'aspect-square rounded-md flex flex-col items-center justify-center text-xs relative transition-colors w-full';

  if (!hasDue) {
    return (
      <div
        data-testid={`calendar-day-${dayNum}`}
        className={`${base} ${isToday ? 'bg-indigo-700 text-white font-semibold' : 'text-slate-600 hover:bg-slate-100'}`}
      >
        <span>{dayNum}</span>
      </div>
    );
  }

  // Side-panel mode: no popover — clicking the day tells the parent to
  // render the list beside the calendar. Selected day gets an indigo ring.
  if (sideMode) {
    const cls = isToday
      ? 'bg-indigo-700 text-white font-semibold'
      : selected
        ? 'bg-amber-100 text-amber-900 border-2 border-amber-500 ring-2 ring-amber-200'
        : 'bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100';
    return (
      <button
        type="button"
        onClick={onSelect}
        data-testid={`calendar-day-due-${dayNum}`}
        className={`${base} ${cls}`}
        title={`${dueList.length} renewal${dueList.length === 1 ? '' : 's'} due — click for details`}
      >
        <span>{dayNum}</span>
        <span className={`absolute bottom-1 h-1.5 w-1.5 rounded-full ${isToday ? 'bg-white' : 'bg-amber-500'}`} aria-hidden />
        {dueList.length > 1 && !isToday && (
          <span className="absolute top-0.5 right-1 text-[9px] font-semibold text-amber-800">{dueList.length}</span>
        )}
      </button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`calendar-day-due-${dayNum}`}
          className={`${base} ${isToday ? 'bg-indigo-700 text-white font-semibold' : 'bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100'}`}
          title={`${dueList.length} renewal${dueList.length === 1 ? '' : 's'} due — click for details`}
        >
          <span>{dayNum}</span>
          <span
            className={`absolute bottom-1 h-1.5 w-1.5 rounded-full ${isToday ? 'bg-white' : 'bg-amber-500'}`}
            aria-hidden
          />
          {dueList.length > 1 && !isToday && (
            <span className="absolute top-0.5 right-1 text-[9px] font-semibold text-amber-800">{dueList.length}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-80 p-0" data-testid={`calendar-popover-${dayNum}`}>
        <div className="p-3 border-b border-slate-100 bg-slate-50">
          <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {dueList.length} renewal{dueList.length === 1 ? '' : 's'} due
          </p>
          <p className="text-xs text-slate-500">Send a reminder with one tap</p>
        </div>
        <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100">
          {dueList.map((r) => (
            <li key={r.policy_id} className="p-3 space-y-2" data-testid={`calendar-renewal-${r.policy_id}`}>
              <div className="min-w-0">
                <p className="font-medium text-slate-900 truncate">{r.client_name || 'Client'}</p>
                <p className="text-xs text-slate-500 truncate">
                  {r.policy_number} · {r.product_type || ''}
                  {typeof r.premium_amount === 'number' && ` · ${inr(r.premium_amount)}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  disabled={sending === `${r.policy_id}-sms` || !r.client_phone}
                  onClick={() => send(r, 'sms')}
                  data-testid={`calendar-send-sms-${r.policy_id}`}
                >
                  <Phone className="h-3.5 w-3.5 mr-1" />
                  {sending === `${r.policy_id}-sms` ? 'Sending…' : 'SMS'}
                </Button>
                <Button
                  size="sm"
                  className="flex-1 bg-green-700 hover:bg-green-800 text-white"
                  disabled={sending === `${r.policy_id}-whatsapp` || !r.client_phone}
                  onClick={() => send(r, 'whatsapp')}
                  data-testid={`calendar-send-wa-${r.policy_id}`}
                >
                  <MessageSquare className="h-3.5 w-3.5 mr-1" />
                  {sending === `${r.policy_id}-whatsapp` ? 'Sending…' : 'WhatsApp'}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Month-view calendar with dots on days that have renewals. Each dot-day is
 * a Popover trigger that opens a list of that day's renewals with one-tap
 * SMS / WhatsApp reminder buttons wired to /api/messages/send.
 */
const RenewalCalendar = ({ renewals = [], onDaySelect, selectedKey }) => {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const sideMode = typeof onDaySelect === 'function';

  const dueByKey = useMemo(() => {
    const map = new Map();
    for (const r of renewals) {
      if (!r.next_due_date) continue;
      const d = new Date(r.next_due_date);
      if (Number.isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const list = map.get(key) || [];
      list.push(r);
      map.set(key, list);
    }
    return map;
  }, [renewals]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isToday = (d) =>
    d && today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  const monthTotal = useMemo(() => {
    let n = 0;
    for (const [k, list] of dueByKey.entries()) {
      const [y, m] = k.split('-').map(Number);
      if (y === year && m === month) n += list.length;
    }
    return n;
  }, [dueByKey, year, month]);

  return (
    <div className="space-y-3" data-testid="renewal-calendar">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {MONTHS[month]} {year}
          </p>
          <p className="text-xs text-slate-500">{monthTotal} renewal{monthTotal === 1 ? '' : 's'} this month</p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendar-prev" aria-label="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} data-testid="calendar-today" className="text-xs">Today</Button>
          <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendar-next" aria-label="Next month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {DOW.map((d) => (
          <div key={d} className="text-[10px] font-medium uppercase tracking-wider text-slate-400 py-1">{d}</div>
        ))}
        {cells.map((d, idx) => {
          if (d === null) return <div key={`b-${idx}`} />;
          const key = `${year}-${month}-${d}`;
          const dueList = dueByKey.get(key) || [];
          return (
            <DayCell
              key={key}
              dueList={dueList}
              dayNum={d}
              isToday={isToday(d)}
              sideMode={sideMode}
              selected={sideMode && selectedKey === key}
              onSelect={sideMode ? () => onDaySelect(key, dueList) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
};

export default RenewalCalendar;
